/**
 * @fileoverview User directory table.
 * @description The User Management grid: its column headers (User with Employee ID beneath, Email,
 * Role, Department, Reporting Manager, State, Phone, Date of Joining, Status, Actions) and one row
 * per user. When there are no users it shows what the page hands it (the page owns the empty
 * state, which belongs to the Sales table kit this feature may not import).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import UserDirectoryRow from '@/features/users/components/UserDirectoryRow'
import {
  USER_TABLE_COLUMN_COUNT,
  USER_TABLE_COLUMN_KEYS,
} from '@/features/users/constants/user-table.constants'

import type { DirectoryUser } from '@/features/users/types/users.types'
import type { UserRowHandlers } from '@/features/users/utils/user-row-actions.utils'
import type { ReactNode } from 'react'

const GRID_COLUMNS = 'grid-cols-[1.8fr_1.7fr_1.2fr_1fr_1.2fr_1fr_1fr_1fr_1fr_0.6fr]'
const LAST_COLUMN_INDEX = USER_TABLE_COLUMN_COUNT - 1

interface UserDirectoryTableProps {
  users: DirectoryUser[]
  handlers: UserRowHandlers
  /** Shown instead of rows when there are no users. */
  emptyState: ReactNode
}

/**
 * @component
 * @param {UserDirectoryTableProps} props - The users, what their actions do, and the empty state.
 * @param {DirectoryUser[]} props.users - The users to list.
 * @param {UserRowHandlers} props.handlers - What each row's actions do.
 * @param {ReactNode} props.emptyState - What to show when there are no users.
 */
function UserDirectoryTable({ users, handlers, emptyState }: UserDirectoryTableProps) {
  const { t } = useTranslation()

  return (
    <div className="grow overflow-x-auto border-t border-divider">
      <div className="min-w-[1400px]">
        <div className={`grid ${GRID_COLUMNS} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}>
          {USER_TABLE_COLUMN_KEYS.map((key, index) => (
            <span
              key={key}
              className={`text-[11px] font-semibold tracking-[0.6px] text-table-header ${
                index === LAST_COLUMN_INDEX ? 'text-right' : ''
              }`}
            >
              {t(`sales.userManagement.columns.${key}`)}
            </span>
          ))}
        </div>

        {users.map((user) => (
          <UserDirectoryRow
            key={user.userId}
            user={user}
            handlers={handlers}
            gridClassName={GRID_COLUMNS}
          />
        ))}

        {users.length === 0 && emptyState}
      </div>
    </div>
  )
}

export default UserDirectoryTable
